import React from 'react';
import { ThemeContext } from 'styled-components';
import { FormProps } from '@omilia/chat-ui';
import { getThemeProp } from 'styles/theme';
import { SBubbleIcon, SImage, SUnreadMessages } from './BubbleWidget.styled';
import { Notifications } from 'types';
import { STORAGE_KEY } from 'constants/browserStorageKeys';
import {
  getSessionStorage,
  removeSessionStorage,
} from 'services/sessionStorage';
import {
  getStorageItem,
  removeStorageItem,
  setStorageItem,
} from 'services/storage';
import { useTranslation } from '@omilia/providers';
import { usePulseAnimations } from './usePulseAnimations';

interface BubbleWidgetProps {
  welcomeForm: FormProps;
  isMinimized: boolean;
  isOpen: boolean;
  isPreviewMode?: boolean;
  unreadMessages: number;
  notificationsConfig: Notifications;
  openChat: () => void;
  openChatBox: () => void;
  minimizeChatBox: (isMinimized: boolean) => void;
  playSilentSound: () => void;
}

const BubbleWidget = (props: BubbleWidgetProps) => {
  const {
    welcomeForm,
    openChatBox,
    openChat,
    isMinimized,
    isOpen,
    isPreviewMode,
    minimizeChatBox,
    unreadMessages,
    notificationsConfig,
    playSilentSound,
  } = props;
  const theme = React.useContext(ThemeContext);
  const { t } = useTranslation();

  const { withBubble, withNumber, withPulseEffect } = notificationsConfig;
  const bubbleUrl = getThemeProp('bubbleUrl', { theme });
  const bubbleAlt = getThemeProp('bubbleAlt', { theme });
  const showBubble = Boolean(withBubble && unreadMessages);
  const showPulseEffect = Boolean(withPulseEffect && unreadMessages);

  const { isPulseActive } = usePulseAnimations({ showPulseEffect });

  const handleOpenChatBox = () => {
    //* This is a hack to make sound notifications work in Safari.
    //* Playing a sound silently once with a user gesture (click on the chatBubble) lets us play a notification sound on receiving messages without any restrictions from Safari.
    playSilentSound();

    setStorageItem(STORAGE_KEY.VIEW_FEEDBACK, false);

    if (welcomeForm) {
      openChatBox();
    } else {
      openChat();
    }
  };

  const handleBubbleClick = () => {
    if (isMinimized || (getStorageItem(STORAGE_KEY.MINIMIZED_CHAT) && isOpen)) {
      minimizeChatBox(false);
      setStorageItem(STORAGE_KEY.MINIMIZED_CHAT, false);
    } else {
      handleOpenChatBox();
      setStorageItem(STORAGE_KEY.MINIMIZED_CHAT, false);
    }

    if (getSessionStorage(STORAGE_KEY.TAB_SESSION_ID)) {
      removeStorageItem(STORAGE_KEY.TAB_SESSION_ID);
    }
    removeSessionStorage(STORAGE_KEY.TAB_SESSION_ID);
  };

  return (
    <SBubbleIcon
      onClick={handleBubbleClick}
      tabIndex={0}
      data-test="icon-chat"
      aria-label={t('widget.openChat')}
      showPulseEffect={showBubble && showPulseEffect && isPulseActive}
      previewMode={isPreviewMode}
    >
      <SImage src={bubbleUrl} alt={bubbleAlt} />
      {showBubble && (
        <SUnreadMessages showPulseEffect={showPulseEffect && isPulseActive}>
          {withNumber ? unreadMessages : ''}
        </SUnreadMessages>
      )}
    </SBubbleIcon>
  );
};

export default BubbleWidget;
